import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text } from '@mantine/core';
import { formatAmount, formatQuantity, parseGermanNumber } from '../format';

/** Keeps an empty cell one line high. Written as a code point: invisible characters do not survive being typed. */
const NBSP = String.fromCharCode(0xa0);

/**
 * A number in a table that turns into a field when clicked, like the other
 * editable cells. Accepts German input ("1.234,56"); empty clears it.
 */
export function AmountCell({
  value,
  onCommit,
  readOnly,
  quantity,
  digits = 2,
  title,
  dimmed,
  bold,
  placeholder = '',
}: {
  value: number | null;
  onCommit?: (next: number | null) => void;
  readOnly?: boolean;
  /** Quantity formatting: up to three decimals, no trailing zeros. */
  quantity?: boolean;
  digits?: number;
  title?: string;
  dimmed?: boolean;
  bold?: boolean;
  placeholder?: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const shown = value === null ? placeholder : quantity ? formatQuantity(value) : formatAmount(value, digits);

  useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    }
  }, [editing]);

  const commit = (): void => {
    const parsed = parseGermanNumber(draft);
    if (parsed === undefined) {
      setInvalid(true);
      return;
    }
    setEditing(false);
    setInvalid(false);
    if (parsed !== value) onCommit?.(parsed);
  };

  if (editing && !readOnly) {
    return (
      <input
        ref={input}
        value={draft}
        inputMode="decimal"
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setEditing(false);
            setInvalid(false);
          }
        }}
        style={{
          width: '100%',
          textAlign: 'right',
          border: `1px solid var(--mantine-color-${invalid ? 'red' : 'brand'}-5)`,
          borderRadius: 4,
          padding: '1px 4px',
          font: 'inherit',
          background: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
        }}
      />
    );
  }
  return (
    <Text
      size="sm"
      ta="right"
      c={dimmed || value === null ? 'dimmed' : undefined}
      fw={bold ? 600 : undefined}
      className={readOnly ? undefined : 'pst-cell'}
      title={title}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onClick={() => {
        if (readOnly) return;
        setDraft(value === null ? '' : quantity ? formatQuantity(value) : formatAmount(value, digits));
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (!readOnly && (e.key === 'Enter' || e.key === 'F2')) {
          e.preventDefault();
          setDraft(value === null ? '' : formatAmount(value, digits));
          setEditing(true);
        }
      }}
      style={{ whiteSpace: 'nowrap', minHeight: 20 }}
    >
      {shown || NBSP}
    </Text>
  );
}
